import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authProvider from '@src/providers/authProvider';
import { AuthLayout } from '../AuthLayout';

// A valid badge is prefixed with `usr-`
const BADGE_PREFIX = 'usr-';

export const BadgeLogin = () => {
  const navigate = useNavigate();
  const [badge, setBadge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = badge.trim();
    if (!trimmed) {
      setError('Please enter your badge.');
      return;
    }
    if (!trimmed.startsWith(BADGE_PREFIX) || trimmed.length === BADGE_PREFIX.length) {
      setError('Badge must start with “usr-”.');
      return;
    }

    setLoading(true);
    try {
      const result = await authProvider.login({ badge: trimmed });
      const redirectTo =
        result && typeof result === 'object' && typeof result.redirectTo === 'string' ?
          result.redirectTo :
          '/';
      // Full navigation so the freshly-stored session is picked up cleanly.
      window.location.replace(redirectTo);
    } catch (err) {
      console.error('Badge sign-in failed:', err);
      setError('Sign-in failed. Check your badge and try again.');
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Sign in with a badge" subcopy="Enter your badge to continue.">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Badge"
          value={badge}
          onChange={e => setBadge(e.target.value)}
          disabled={loading}
          autoFocus
          autoComplete="off"
          sx={{ mb: 1.5 }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ height: 44, fontSize: 15 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
        </Button>
      </Box>

      <Button
        fullWidth
        variant="text"
        sx={{ height: 44, mt: 1.5 }}
        onClick={() => navigate('/login')}
      >
        Back to sign in
      </Button>
    </AuthLayout>
  );
};
